import logoSvg from "./logo.svg?raw";
import cMarkSvg from "./c-mark.svg?raw";
import coverUrl from "./first-page-img.jpg";
import templateHtml from "./renewal.html?raw";
import { CLASSIFICATION, emptyRenewal } from "./emptyRenewal.js";
import { renderMustache } from "./renderMustache.js";

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function svgUri(svg) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function absoluteAsset(url) {
    if (!url || typeof window === "undefined") return url || "";
    try {
        return new URL(url, window.location.href).href;
    } catch {
        return url;
    }
}

function formatDate(value) {
    const raw = String(value || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const [year, month, day] = raw.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
}

function filled(value, fallback) {
    const text = String(value ?? "").trim();
    return text || fallback;
}

function dataTable(headers, body) {
    const head = headers.map((header) => `<th>${escapeHtml(header)}</th>`).join("");
    return `<div class="tpl-data is-excel-table"><div class="tpl-data-frame"><table class="tpl-data-table"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div></div>`;
}

function cell(table, index, field, value) {
    return `<td data-aso-field="${table}-${index}-${field}">${escapeHtml(value)}</td>`;
}

function renewalTables(renewal) {
    const benchmarks = (renewal.benchmarks || []).map((row, index) => (
        `<tr>${cell("benchmarks", index, "metric", row.metric)}${cell("benchmarks", index, "centivo", row.centivo)}${cell("benchmarks", index, "milliman", row.milliman)}${cell("benchmarks", index, "regional", row.regional)}</tr>`
    )).join("");
    const impact = (renewal.impact || []).map((row, index) => (
        `<tr>${cell("impact", index, "metric", row.metric)}${cell("impact", index, "ppo", row.ppo)}${cell("impact", index, "centivo", row.centivo)}${cell("impact", index, "difference", row.difference)}</tr>`
    )).join("");
    const partnership = (renewal.partnership || []).map((row, index) => (
        `<tr>${cell("partnership", index, "metric", row.metric)}${cell("partnership", index, "partnership", row.partnership)}${cell("partnership", index, "ppo", row.ppo)}</tr>`
    )).join("");
    const rates = (renewal.rates || []).map((row, index) => (
        `<tr>${cell("rates", index, "line", row.line)}${cell("rates", index, "current", row.current)}${cell("rates", index, "proposed", row.proposed)}${cell("rates", index, "change", row.change)}</tr>`
    )).join("");
    return {
        benchmark: dataTable(["Metric", "Centivo Book", "Milliman Benchmark", "Regional Market Benchmark"], benchmarks),
        impact: dataTable(["Metric", "Traditional PPO Estimate", "Centivo Actual", "Difference"], impact),
        partnership: dataTable(["Metric", "Partnership Plan", "PPO"], partnership),
        rates: dataTable(["", "Current Rate", "Proposed Rate", "Change"], rates),
    };
}

export function renderRenewal(draft) {
    const renewal = { ...emptyRenewal(), ...(draft?.renewal || {}) };
    const companyName = draft?.companyName?.trim() || "[Company Name]";
    const classification = CLASSIFICATION[renewal.classification] || CLASSIFICATION.fair;
    return renderMustache(templateHtml, {
        companyName,
        planYear: filled(renewal.planYear, "[Plan Year]"),
        effectiveDateLabel: formatDate(draft?.effectiveDate) || "[MM/DD/YYYY]",
        priorIncrease: filled(renewal.priorIncrease, "[X]"),
        priorCarrier: filled(renewal.priorCarrier, "[prior carrier]"),
        switchDriver: filled(renewal.switchDriver, "[the reason you switched]"),
        nextSteps: filled(renewal.nextSteps, "Add the decision this client should make, and by when."),
        yearInReview: renewal.yearInReview || "",
        glanceValue: filled(renewal.glanceValue, "—"),
        glanceAdoption: filled(renewal.glanceAdoption, "—"),
        glanceClaims: filled(renewal.glanceClaims, "—"),
        glanceClinical: filled(renewal.glanceClinical, "—"),
        classificationLabel: classification.label,
        classificationNote: classification.note,
        driverMedical: renewal.driverMedical || "",
        driverPharmacy: renewal.driverPharmacy || "",
        driverStopLoss: renewal.driverStopLoss || "",
        driverAdmin: renewal.driverAdmin || "",
        roiIncrease: filled(renewal.roiIncrease, "[X]"),
        roiSavings: filled(renewal.roiSavings, "[Y]"),
        avoided: renewal.avoided || "",
        includeMultiYear: Boolean(renewal.includeMultiYear),
        multiYear: filled(renewal.multiYear, "Add the year-over-year savings for a group with two or more renewal cycles."),
        isBhcg: renewal.population === "bhcg",
        renewalOptions: renewal.renewalOptions || "",
        recFinancial: renewal.recFinancial || "",
        recClinical: renewal.recClinical || "",
        recEngagement: renewal.recEngagement || "",
        recOperational: renewal.recOperational || "",
        headed: filled(renewal.headed, "Add two or three sentences about what is relevant for this client."),
        decisionDate: filled(renewal.decisionDate, "[DATE]"),
        enrollmentWindow: filled(renewal.enrollmentWindow, "[DATE RANGE]"),
        implementation: filled(renewal.implementation, "[DETAIL]"),
        includeAppendix: Boolean(renewal.includeAppendix),
        tables: renewalTables(renewal),
        assets: {
            logo: svgUri(logoSvg),
            cMark: svgUri(cMarkSvg),
            coverHero: absoluteAsset(coverUrl),
        },
        pageBreak: '<div class="tpl-page-break-marker" data-page-break="true"></div>',
    });
}
